import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useAuthContext } from './useAuthContext';

export const useAddresses = () => {
  const { session } = useAuthContext();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session) {
      setAddresses([]);
      setLoading(false);
      return;
    }

    const fetchAddresses = async () => {
      try {
        setLoading(true);
        console.log('Fetching addresses for user:', session.user.id);

        // Fetch addresses from the database
        const { data, error: fetchError } = await supabase
          .from('shipping_information')
          .select('*')
          .eq('profile_id', session.user.id)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: false });

        console.log('Addresses response - data:', data, 'error:', fetchError);

        if (fetchError) {
          console.error('Error fetching addresses:', fetchError);
          throw fetchError;
        }

        if (data) {
          console.log(`Found ${data.length} addresses:`, data);
          setAddresses(data);
        } else {
          console.log('No addresses found');
          setAddresses([]);
        }
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setError(err.message);
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [session]);

  const addAddress = async (addressData) => {
    if (!session) {
      throw new Error('You must be logged in to add addresses');
    }

    try {
      console.log('Adding address:', addressData);
      const normalizedAddress = {
        ...addressData,
        // Keep compatibility with old payloads that may use `note`
        delivery_notes:
          (addressData?.delivery_notes ?? addressData?.note ?? '').toString().trim() || null,
      };

      // If this is set as default, unset other defaults
      if (normalizedAddress.is_default) {
        await supabase
          .from('shipping_information')
          .update({ is_default: false })
          .eq('profile_id', session.user.id);
      }

      const { data, error: insertError } = await supabase
        .from('shipping_information')
        .insert({
          profile_id: session.user.id,
          ...normalizedAddress
        })
        .select()
        .single();

      console.log('Add address response - data:', data, 'error:', insertError);

      if (insertError) {
        throw insertError;
      }

      // Refresh addresses list
      const { data: newData } = await supabase
        .from('shipping_information')
        .select('*')
        .eq('profile_id', session.user.id)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      setAddresses(newData || []);

      return data;
    } catch (err) {
      console.error('Error adding address:', err);
      throw err;
    }
  };

  const updateAddress = async (addressId, addressData) => {
    if (!session) {
      throw new Error('You must be logged in to update addresses');
    }

    try {
      console.log('Updating address:', addressId, addressData);
      const normalizedAddress = {
        ...addressData,
        // Keep compatibility with old payloads that may use `note`
        delivery_notes:
          (addressData?.delivery_notes ?? addressData?.note ?? '').toString().trim() || null,
      };

      // If this is set as default, unset other defaults
      if (normalizedAddress.is_default) {
        await supabase
          .from('shipping_information')
          .update({ is_default: false })
          .eq('profile_id', session.user.id)
          .neq('id', addressId);
      }

      const { data, error: updateError } = await supabase
        .from('shipping_information')
        .update({
          ...normalizedAddress,
          updated_at: new Date().toISOString()
        })
        .eq('id', addressId)
        .select()
        .single();

      console.log('Update address response - data:', data, 'error:', updateError);

      if (updateError) {
        throw updateError;
      }

      // Refresh addresses list
      const { data: newData } = await supabase
        .from('shipping_information')
        .select('*')
        .eq('profile_id', session.user.id)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      setAddresses(newData || []);

      return data;
    } catch (err) {
      console.error('Error updating address:', err);
      throw err;
    }
  };

  const deleteAddress = async (addressId) => {
    if (!session) {
      throw new Error('You must be logged in to delete addresses');
    }

    try {
      console.log('Deleting address:', addressId);

      const { error: deleteError } = await supabase
        .from('shipping_information')
        .delete()
        .eq('id', addressId);

      console.log('Delete address response - error:', deleteError);

      if (deleteError) {
        throw deleteError;
      }

      // Remove from local state
      setAddresses(prevAddresses => 
        prevAddresses.filter(addr => addr.id !== addressId)
      );

      return true;
    } catch (err) {
      console.error('Error deleting address:', err);
      throw err;
    }
  };

  const getDefaultAddress = () => {
    return addresses.find(addr => addr.is_default) || addresses[0];
  };

  return {
    addresses,
    loading,
    error,
    addAddress,
    updateAddress,
    deleteAddress,
    getDefaultAddress
  };
};
